import { useEffect, useState } from 'react';
import { api } from '@/lib/api-client';
import { useAuthStore } from '@/store/authStore';

export interface LookupItem {
  id: number;
  label: string;
  /**
   * Code stable quand le referentiel en a un : les reseaux bancaires
   * (WIRE / DIRECT, #153), et les pays, dont c'est meme la cle primaire —
   * `id` y vaut zero, il n'y a pas d'identifiant numerique (#222).
   */
  code?: string;
}

type LookupKey =
  | 'currencies'
  | 'countries'
  | 'person-roles'
  | 'beneficiary-types'
  | 'bank-networks'
  | 'legal-forms'
  | 'business-sectors'
  | 'business-activities';

/**
 * Tiny per-tab cache keyed by lookup name. Each dropdown stays in sync with
 * the CBS /api/lookups/* endpoints (themselves backed by a 5-minute server-
 * side cache), so we keep this client copy intentionally simple.
 *
 * The backend scopes results by the JWT's tenant_cod, so the cached entries
 * are implicitly per-tenant. On tenant switch we flush both maps via
 * `clearLookupCache()` — wired to the `cbs:tenant-switched` window event in
 * AppShell (see App.tsx) — so the next render refetches for the new tenant.
 */
const cache = new Map<string, LookupItem[]>();
const inflight = new Map<string, Promise<LookupItem[]>>();
let generation = 0;

export function clearLookupCache(): void {
  generation += 1;
  cache.clear();
  inflight.clear();
}

async function load(key: LookupKey, cacheKey: string): Promise<LookupItem[]> {
  const cached = cache.get(cacheKey);
  if (cached) return cached;
  const pending = inflight.get(cacheKey);
  if (pending) return pending;
  const requestGeneration = generation;
  const promise = api
    .get<LookupItem[]>(`/api/lookups/${key}`)
    .then((data) => {
      if (requestGeneration === generation) {
        cache.set(cacheKey, data);
        inflight.delete(cacheKey);
      }
      return data;
    })
    .catch((err) => {
      if (requestGeneration === generation) inflight.delete(cacheKey);
      throw err;
    });
  inflight.set(cacheKey, promise);
  return promise;
}

export function useLookup(key: LookupKey): {
  items: LookupItem[];
  loading: boolean;
  error: string | null;
} {
  const tenantCod = useAuthStore((state) => state.user?.tenant_cod ?? '');
  const cacheKey = `${tenantCod}:${key}`;
  const [scope, setScope] = useState(cacheKey);
  const [items, setItems] = useState<LookupItem[]>(() => cache.get(cacheKey) ?? []);
  const [loading, setLoading] = useState(!cache.has(cacheKey));
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const onTenantSwitch = () => {
      clearLookupCache();
      setItems([]);
      setError(null);
      setLoading(true);
      setRevision((value) => value + 1);
    };
    window.addEventListener('cbs:tenant-switched', onTenantSwitch);
    return () => window.removeEventListener('cbs:tenant-switched', onTenantSwitch);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setScope(cacheKey);
    if (cache.has(cacheKey)) {
      setItems(cache.get(cacheKey) ?? []);
      setLoading(false);
      setError(null);
      return () => {
        cancelled = true;
      };
    }
    setItems([]);
    setLoading(true);
    const requestGeneration = generation;
    load(key, cacheKey)
      .then((data) => {
        if (!cancelled && requestGeneration === generation) {
          setItems(data);
          setLoading(false);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled && requestGeneration === generation) {
          setError(err instanceof Error ? err.message : 'Failed to load lookup');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [key, cacheKey, revision]);

  return {
    items: scope === cacheKey ? items : [],
    loading: loading || scope !== cacheKey,
    error: scope === cacheKey ? error : null,
  };
}
